import { useMemo, useCallback } from "react";
import { Typography, Alert } from "@mui/material";
import { useRelatedDeals, useAuthentication } from "hooks";
import { DealCard, BaseGrid, DealGridSkeleton } from "components";
import { getCurrentDay } from "utilities";

const RELATED_GRID_COLUMNS = {
  xs: "1fr",
  sm: "repeat(2, 1fr)",
  md: "repeat(3, 1fr)",
  lg: "repeat(3, 1fr)",
};

export function RelatedDealGrid({ currentDeal }) {
  const { relatedDeals, loading, error, likeDeal, unlikeDeal } =
    useRelatedDeals(currentDeal);
  const { user } = useAuthentication();

  const handleLike = useCallback(
    (id, uid) => likeDeal(id),
    [likeDeal],
  );

  const handleUnlike = useCallback(
    (id, uid) => unlikeDeal(id),
    [unlikeDeal],
  );

  const renderedDeals = useMemo(() => {
    return relatedDeals.map((deal) => {
      const isLiked = !!(user && deal.likedBy?.includes(user.uid));
      const isOwner = user?.uid === deal.userId;

      return (
        <DealCard
          key={deal.id}
          deal={deal}
          onLike={handleLike}
          onUnlike={handleUnlike}
          isLiked={isLiked}
          currentUser={user}
          isOwner={isOwner}
        />
      );
    });
  }, [relatedDeals, user, handleLike, handleUnlike]);

  if (loading) {
    // No week buttons in related grid — showWeekButtons={false}
    return <DealGridSkeleton showWeekButtons={false} />;
  }

  if (error) {
    return (
      <Alert severity="error">
        <Typography variant="body1">Failed to load deals</Typography>
        <Typography variant="body2">{error}</Typography>
      </Alert>
    );
  }

  if (relatedDeals.length === 0) {
    return (
      <Alert severity="info">
        {currentDeal.title
          ? `No deals found matching "${currentDeal.title}"`
          : `No deals available for ${getCurrentDay()}.`}
      </Alert>
    );
  }

  return (
    <BaseGrid columns={RELATED_GRID_COLUMNS}>
      {renderedDeals}
    </BaseGrid>
  );
}